/**
 * Generic per-user preference hook. Reads/writes a single JSONB row in
 * `user_preferences` keyed by (auth.uid(), key). RLS scopes to the current user
 * so each teammate has their own state.
 *
 * Usage:
 *   const { value, setValue, isLoaded } = useUserPreference<string[]>(
 *     'wo:collapsed-stages',
 *     [],
 *   )
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'
import { toJson } from '@/lib/supabase/typing'
import { useAuthStore } from '@/stores'

export const userPreferenceKeys = {
  all: ['user-preferences'] as const,
  key: (k: string) => ['user-preferences', k] as const,
}

export function useUserPreference<T>(key: string, defaultValue: T) {
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: userPreferenceKeys.key(key),
    enabled: !!userId,
    staleTime: Infinity,
    queryFn: async (): Promise<T> => {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('value')
        .eq('user_id', userId!)
        .eq('key', key)
        .maybeSingle()
      if (error) throw error
      return (data?.value as T | undefined) ?? defaultValue
    },
  })

  // Local override (set once the user changes something this mount) layered
  // over the cached server value. Deriving `value` from query.data directly —
  // instead of mirroring it into state via an effect — means the very first
  // render after a remount already shows the saved value, and `isLoaded` is
  // never true while `value` still holds the default. (The old effect-sync
  // left a one-commit window where isLoaded=true but value=default; effects
  // that gate on isLoaded — e.g. the WO board's collapse seed — ran against
  // the default and clobbered the saved state on every mount.)
  const [local, setLocal] = useState<{ v: T } | null>(null)
  const value: T = local ? local.v : query.data !== undefined ? query.data : defaultValue

  const mut = useMutation({
    mutationFn: async (value: T) => {
      const { error } = await supabase.rpc('set_user_preference', {
        p_key: key,
        p_value: toJson(value),
      })
      if (error) throw error
      return value
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: userPreferenceKeys.key(key) })
      const prev = qc.getQueryData<T>(userPreferenceKeys.key(key))
      qc.setQueryData(userPreferenceKeys.key(key), next)
      return { prev }
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev !== undefined) {
        qc.setQueryData(userPreferenceKeys.key(key), ctx.prev)
        setLocal({ v: ctx.prev })
      }
    },
  })

  // Callers pass inline defaults (`[]`, `{}`), so read the latest through a ref
  // rather than making setValue's identity churn every render.
  const defaultRef = useRef(defaultValue)
  defaultRef.current = defaultValue
  const { mutate } = mut
  const setValue = useCallback(
    (updater: T | ((prev: T) => T)) => {
      setLocal((prev) => {
        const cached = qc.getQueryData<T>(userPreferenceKeys.key(key))
        const base = prev ? prev.v : cached !== undefined ? cached : defaultRef.current
        const next =
          typeof updater === 'function'
            ? (updater as (p: T) => T)(base)
            : updater
        mutate(next)
        return { v: next }
      })
    },
    [mutate, qc, key],
  )

  return useMemo(
    () => ({
      value,
      setValue,
      isLoaded: query.isSuccess || !userId,
      isLoading: query.isLoading,
    }),
    [value, setValue, query.isSuccess, query.isLoading, userId],
  )
}
